import React, { useState, useEffect } from "react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Chip,
    Button,
    Paper
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Link as RouterLink } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
    root: {
        background: '#f5f8f5',
        minHeight: '100vh',
        padding: theme.spacing(3),
    },
    welcomeCard: {
        background: '#00624d',
        color: 'white',
        marginBottom: theme.spacing(3),
    },
    statsCard: {
        marginBottom: theme.spacing(3),
    },
    cohortCard: {
        marginBottom: theme.spacing(2),
        transition: 'transform 0.2s',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4],
        },
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
    },
    surveyButton: {
        marginTop: theme.spacing(1),
    },
    memberCount: {
        marginTop: theme.spacing(1),
    }
}));

function StudentDashboard(props) {
    const classes = useStyles();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const response = await fetch('/api/user/dashboard', {
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                
                if (!response.ok) {
                    throw new Error('Failed to fetch dashboard data');
                }
                
                const data = await response.json();
                setDashboardData(data);
            } catch (err) {
                console.error('Dashboard fetch error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div style={{background: 'white'}}>
                <NavBar page="StudentDashboard" isLoggedIn={props.isLoggedIn} />
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
                <Footer/>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{background: 'white'}}>
                <NavBar page="StudentDashboard" isLoggedIn={props.isLoggedIn} />
                <div className={classes.root}>
                    <Typography variant="h6" color="error" align="center">
                        Error loading dashboard: {error}
                    </Typography>
                </div>
                <Footer/>
            </div>
        );
    }

    const { user, cohorts, stats } = dashboardData;

    return (
        <div style={{background: 'white'}}>
            <NavBar page="StudentDashboard" isLoggedIn={props.isLoggedIn} />
            <div className={classes.root}>
                <Grid container spacing={3}>
                    {/* Welcome Card */}
                    <Grid item xs={12}>
                        <Card className={classes.welcomeCard}>
                            <CardContent>
                                <Typography variant="h4" gutterBottom>
                                    Welcome back, {user.name}!
                                </Typography>
                                <Typography variant="body1">
                                    Member since {formatDate(user.accountCreated)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Stats Cards */}
                    <Grid item xs={12} md={6}>
                        <Card className={classes.statsCard}>
                            <CardContent>
                                <Typography variant="h6" style={{ color: '#c3451e' }} gutterBottom>
                                    Surveys Completed
                                </Typography>
                                <Typography variant="h3">
                                    {stats.surveysCompleted}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card className={classes.statsCard}>
                            <CardContent>
                                <Typography variant="h6" style={{ color: '#c3451e' }} gutterBottom>
                                    Cohorts Joined
                                </Typography>
                                <Typography variant="h3">
                                    {stats.cohortsJoined}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* My Cohorts Section */}
                    <Grid item xs={12}>
                        <Typography variant="h5" gutterBottom>
                            My Cohorts
                        </Typography>
                        {cohorts.length === 0 ? (
                            <Paper className={classes.emptyState}>
                                <Typography variant="h6" gutterBottom>
                                    No cohorts yet
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                    You haven't joined any cohorts yet. Ask your instructor for a cohort invitation code.
                                </Typography>
                            </Paper>
                        ) : (
                            <Grid container spacing={2}>
                                {cohorts.map((cohort) => (
                                    <Grid item xs={12} md={6} key={cohort.id}>
                                        <Card className={classes.cohortCard}>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    {cohort.name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" paragraph>
                                                    {cohort.description || 'No description available'}
                                                </Typography>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Chip 
                                                        label={`Joined ${formatDate(cohort.joined_date)}`}
                                                        size="small"
                                                        style={{ color: '#c3451e', borderColor: '#c3451e' }}
                                                        variant="outlined"
                                                    />
                                                    <Typography variant="caption" className={classes.memberCount}>
                                                        {cohort.memberCount || 0} members
                                                    </Typography>
                                                </Box>
                                                {cohort.survey_link && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        className={classes.surveyButton}
                                                        component={RouterLink}
                                                        to={`/questions?cohortId=${cohort.id}`}
                                                        style={{ backgroundColor: '#c3451e', color: 'white' }}
                                                    >
                                                        Take Survey
                                                    </Button>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </div>
            <Footer/>
        </div>
    );
}

export default StudentDashboard;
